import React, { useState } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { TextField, TextFieldPassword, Button, Typography } from 'ui-kit';
import { initialValues, loginFormValidationSchema } from './utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from 'contexts/auth/auth';
import './LoginForm.scss';
import { message } from 'antd';
import ErrorMessage from 'components/ErrorMessage';

export default function LoginForm() {
  const { hostLogin } = useAuth();
  const history = useHistory();
  const [attempts, setAttempts] = useState<Record<string, number>>({});

  const { control, handleSubmit, formState, setError, setValue, getValues, clearErrors } = useForm({
    mode: 'onSubmit',
    defaultValues: initialValues,
    resolver: yupResolver(loginFormValidationSchema),
    shouldFocusError: false,
  });

  const goToForgotPassword = () => {
    const { email } = getValues();
    history.push({ pathname: '/forgot-password', state: { email } });
  };

  const handleAttempts = (email: string) => {
    const tempAttemps = { ...attempts };
    tempAttemps[email] = tempAttemps[email] ? tempAttemps[email] + 1 : 1;
    if (tempAttemps[email] > 3) {
      message.error(
        <ErrorMessage
          message="It looks like you’re having some trouble logging in. Would you like to"
          action={goToForgotPassword}
          actionLabel="reset your password?"
        />,
        3,
      );
    }
    setError('email', { type: 'custom' });
    setError('password', {
      type: 'custom',
      message: 'The email and password combination you entered is incorrect. Please try again.',
    });
    setValue('password', '');
    setAttempts(tempAttemps);
  };

  const onSubmit = async ({ email, password }) => {
    try {
      await hostLogin(email, password);
      history.push('/overview/stores/');
    } catch (error) {
      handleAttempts(email);
    }
  };

  return (
    <div className="LoginForm">
      <form className="LoginForm-fields" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="email"
          render={({ field: { ref, ...rest }, fieldState }) => (
            <TextField
              label="Email"
              required
              fullWidth
              inputRef={ref}
              error={Boolean(fieldState.error)}
              errorMessage={fieldState.error?.message}
              {...rest}
            />
          )}
        />
        <Controller
          control={control}
          name="password"
          render={({ field: { ref, onChange, ...rest }, fieldState, formState }) => (
            <TextFieldPassword
              onChange={(e) => {
                if (formState.errors.email && formState.errors.email.type === 'custom')
                  clearErrors();
                onChange(e);
              }}
              label={
                <span>
                  Password{' '}
                  <Button
                    onClick={goToForgotPassword}
                    variant="text"
                    color="primary"
                    className="LoginForm-forgot-password"
                  >
                    Forgot password?
                  </Button>
                </span>
              }
              required
              fullWidth
              inputRef={ref}
              error={Boolean(fieldState.error)}
              errorMessage={fieldState.error?.message}
              {...rest}
            />
          )}
        />

        <Button
          className="LoginForm-fields__login-button"
          type="submit"
          variant="contained"
          fullWidth
          disabled={!_isEmpty(formState.errors)}
          loading={formState.isSubmitting}
        >
          Sign in with email
        </Button>
      </form>
      <Typography variant="body2" className="LoginForm-footer">
        Don&lsquo;t have an account? &nbsp;
        <Button to="/signup" variant="text" color="primary">
          Sign up here
        </Button>
      </Typography>
    </div>
  );
}
